import {
  Alert, Box, Button, Checkbox, CircularProgress, Divider, FormControlLabel, IconButton,
  InputAdornment, Link, Stack, TextField, Typography,
} from '@mui/material';
import {
  ArrowForwardRounded, AutoAwesomeRounded, BoltRounded, ConfirmationNumberRounded, FlightTakeoffRounded,
  GroupsRounded, InsightsRounded, LockOutlined, LuggageRounded, MailOutlineRounded, PublicRounded,
  ShieldOutlined, SupportAgentRounded, Visibility, VisibilityOff,
} from '@mui/icons-material';
import { Controller } from 'react-hook-form';
import { motion, useReducedMotion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { APP_NAME } from '@/config/productBrand';
import { useLoginForm, type LoginBrand, type LoginTemplateProps } from '../useLoginForm';
import LoginOtpPanel from '../LoginOtpPanel';
import HeroSky from './HeroSky';
import FeatureChip from './FeatureChip';
import StatisticCard from './StatisticCard';

const APP_VERSION = 'v2.1.0';

const FEATURES = [
  { title: 'AI Powered', sub: 'Smart Automation', icon: <AutoAwesomeRounded /> },
  { title: 'ERP & Leads', sub: 'Manage Customers', icon: <GroupsRounded /> },
  { title: 'Bookings', sub: 'Flights, Hotels, Holidays', icon: <LuggageRounded /> },
  { title: 'Analytics', sub: 'Insights & Reports', icon: <InsightsRounded /> },
];

const STATS = [
  { value: '150+', label: 'Countries', icon: <PublicRounded />, color: '#38BDF8' },
  { value: '50K+', label: 'Bookings', icon: <ConfirmationNumberRounded />, color: '#A78BFA' },
  { value: '10K+', label: 'Travel Agents', icon: <SupportAgentRounded />, color: '#34D399' },
  { value: '99.9%', label: 'Uptime', icon: <BoltRounded />, color: '#FBBF24' },
];

const glassPanel = {
  bgcolor: 'rgba(9,14,32,0.5)',
  border: '1px solid rgba(255,255,255,0.14)',
  backdropFilter: 'blur(18px)',
  WebkitBackdropFilter: 'blur(18px)',
  boxShadow: '0 16px 44px rgba(2,6,23,0.5)',
};

/**
 * Skyline — the TravelERP sign-in: one cinematic photograph across the whole screen, the hero on the left
 * (~63%) and a glass card on the right (~37%). On a company's own domain the company's name and logo take
 * TravelERP's place, and TravelERP's own marketing (the stats, "Create Workspace") is left out.
 */
export default function SkylineLogin({ brand, initialEmail, preview }: LoginTemplateProps) {
  const reduce = useReducedMotion();

  return (
    <Box
      component={motion.main}
      initial={reduce ? false : { opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.6 }}
      sx={{ position: 'relative', minHeight: '100vh', width: '100%', overflow: 'hidden', bgcolor: '#070B19' }}
    >
      {/* Layer 0 — the photograph */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url("/images/login-hero.jpg")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          transform: 'scale(1.04)', // hides the blur edge
          filter: 'blur(0.6px)',
        }}
      />
      {/* Layer 1 — dark-blue overlay, edge legibility gradients and a soft vignette */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute', inset: 0,
          background:
            'radial-gradient(70% 60% at 82% 2%, rgba(168,110,196,0.28), transparent 60%),' +
            'linear-gradient(90deg, rgba(10,12,42,0.66) 0%, rgba(10,12,42,0.24) 34%, rgba(14,12,40,0.2) 54%, rgba(20,16,52,0.5) 100%),' +
            'linear-gradient(180deg, rgba(66,40,120,0.28) 0%, rgba(26,24,66,0.1) 38%, transparent 55%, rgba(5,8,22,0.62) 100%),' +
            'radial-gradient(130% 100% at 50% 42%, transparent 55%, rgba(4,6,20,0.6) 100%)',
        }}
      />
      <Box aria-hidden sx={{ position: 'absolute', inset: 0, background: 'rgba(23,37,84,0.18)', mixBlendMode: 'multiply' }} />

      {/* Layer 2 — content */}
      <Box
        sx={{
          position: 'relative', minHeight: '100vh', width: '100%',
          display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'stretch',
        }}
      >
        <Box sx={{ width: { xs: '100%', md: '58%', lg: '63%' }, flexShrink: 0 }}>
          <SkylineHero brand={brand} />
        </Box>

        {/* Right — glass card (moves above the hero on mobile) */}
        <Box
          component={motion.div}
          initial={reduce ? false : { opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          sx={{
            order: { xs: -1, md: 0 },
            width: { xs: '100%', md: '42%', lg: '37%' },
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: { xs: 2.5, sm: 4, lg: 5 },
          }}
        >
          <SkylineCard brand={brand} initialEmail={initialEmail} preview={preview} />
        </Box>
      </Box>
    </Box>
  );
}

/** The left hero — headline, subtitle, glass feature bar and (TravelERP only) the stats. */
function SkylineHero({ brand }: { brand: LoginBrand | null }) {
  const reduce = useReducedMotion();
  const slide = (delay: number) =>
    reduce ? {} : { initial: { opacity: 0, x: -28 }, animate: { opacity: 1, x: 0 }, transition: { duration: 0.6, delay } };

  const headlineSx = {
    fontWeight: 800,
    lineHeight: 1.04,
    letterSpacing: '-0.03em',
    fontSize: { xs: 32, sm: 40, lg: 50, xl: 50 },
    textShadow: '0 2px 30px rgba(0,0,0,0.45)',
  };

  return (
    <Box
      sx={{
        position: 'relative', minHeight: { md: '100vh' }, height: '100%', color: '#fff',
        display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
        px: { xs: 3.5, sm: 6, lg: 8 }, py: { xs: 6, md: 6, lg: 7 }, gap: 4, overflow: 'hidden',
      }}
    >
      <HeroSky reduce={!!reduce} />

      {/* Top — headline + subtitle (nudged down so the "New York" map label stays visible above it) */}
      <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 620, mt: { xs: 4, md: 9 } }}>
        <Box component={motion.div} {...slide(0.05)}>
          {brand ? (
            <Typography sx={{ ...headlineSx, wordBreak: 'break-word' }}>{brand.companyName}</Typography>
          ) : (
            <Typography sx={headlineSx}>
              <Box component="span" sx={{ display: 'block' }}>World&apos;s First</Box>
              <Box component="span" sx={{ display: 'block' }}>
                <Box
                  component="span"
                  sx={{ background: 'linear-gradient(90deg,#4F46E5,#2563EB 45%,#06B6D4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}
                >
                  AI
                </Box>{' '}
                Travel
              </Box>
              <Box component="span" sx={{ display: 'block' }}>Operating System</Box>
            </Typography>
          )}
        </Box>

        <Box component={motion.div} {...slide(0.18)}>
          <Typography sx={{ mt: 3, fontSize: { xs: 15, lg: 17.5 }, color: 'rgba(226,232,240,0.85)', lineHeight: 1.6, maxWidth: 440, textShadow: '0 1px 12px rgba(0,0,0,0.4)' }}>
            {brand ? 'Enquiries, bookings and travellers — all in one place.' : 'All your travel business. All in one intelligent platform.'}
            <Box component="span" sx={{ display: 'block' }}>{brand ? 'Sign in to continue.' : 'Automate. Manage. Grow.'}</Box>
          </Typography>
        </Box>
      </Box>

      {/* Bottom — feature bar and stats */}
      <Stack spacing={2.5} sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          component={motion.div}
          {...slide(0.3)}
          sx={{ ...glassPanel, borderRadius: 3, display: { xs: 'none', sm: 'flex' }, alignItems: 'stretch', maxWidth: 720, overflow: 'hidden' }}
        >
          {FEATURES.map((f, i) => (
            <Box key={f.title} sx={{ display: 'flex', flex: '1 1 0', minWidth: 0 }}>
              {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />}
              <FeatureChip title={f.title} sub={f.sub} icon={f.icon} delay={0.34 + i * 0.06} />
            </Box>
          ))}
        </Box>

        {/* TravelERP's own figures — never shown under a company's name. */}
        {!brand && (
          <Box
            component={motion.div}
            {...slide(0.42)}
            sx={{ ...glassPanel, borderRadius: 3, display: 'flex', alignItems: 'stretch', maxWidth: 520, overflow: 'hidden' }}
          >
            {STATS.map((s, i) => (
              <Box key={s.label} sx={{ display: 'flex', flex: '1 1 0', minWidth: 0 }}>
                {i > 0 && <Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(255,255,255,0.12)' }} />}
                <StatisticCard value={s.value} label={s.label} icon={s.icon} color={s.color} />
              </Box>
            ))}
          </Box>
        )}
      </Stack>
    </Box>
  );
}

/** The glass sign-in card. */
function SkylineCard({ brand, initialEmail, preview }: LoginTemplateProps) {
  const reduce = useReducedMotion();
  const { form, onSubmit, notice, error, busy, showPwd, toggleShowPwd, remember, setRemember, otp } =
    useLoginForm(initialEmail, preview);

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      height: 56,
      borderRadius: 3,
      bgcolor: 'rgba(255,255,255,0.05)',
      color: '#fff',
      transition: 'box-shadow .2s ease, border-color .2s ease, background-color .2s ease',
      '& fieldset': { borderColor: 'rgba(255,255,255,0.16)', transition: 'border-color .2s ease' },
      '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
      '&.Mui-focused fieldset': { borderColor: 'rgba(120,140,255,0.9)', borderWidth: '1px' },
      '&.Mui-focused': { boxShadow: '0 0 0 4px rgba(79,70,229,0.22)', bgcolor: 'rgba(255,255,255,0.07)' },
      '&.Mui-error fieldset': { borderColor: 'rgba(248,113,113,0.7)' },
    },
    '& .MuiOutlinedInput-input': { color: '#fff', fontSize: 15 },
    '& .MuiOutlinedInput-input::placeholder': { color: 'rgba(203,213,225,0.5)', opacity: 1 },
    // Keep browser autofill on-theme: no white box — the field stays dark-translucent with white text.
    '& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus, & input:-webkit-autofill:active': {
      WebkitTextFillColor: '#fff',
      caretColor: '#fff',
      borderRadius: 'inherit',
      transition: 'background-color 600000s 0s, color 600000s 0s',
    },
    '& .MuiInputAdornment-root svg': { color: 'rgba(203,213,225,0.72)', fontSize: 20 },
    '& .MuiFormHelperText-root': { color: '#FCA5A5', ml: 0.5 },
  };

  const label = (text: string) => (
    <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'rgba(226,232,240,0.9)', mb: 0.85 }}>{text}</Typography>
  );

  return (
    <Box sx={{ width: '100%', maxWidth: 440 }}>
      <Box
        component={motion.div}
        initial={reduce ? false : { opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 0.25 }}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: '24px',
          background: 'linear-gradient(160deg, rgba(46,58,128,0.42) 0%, rgba(17,24,54,0.5) 55%, rgba(12,18,44,0.55) 100%)',
          border: '1px solid rgba(255,255,255,0.15)',
          backdropFilter: 'blur(30px)',
          WebkitBackdropFilter: 'blur(30px)',
          boxShadow: '0 40px 90px rgba(2,6,23,0.55), inset 0 1px 0 rgba(255,255,255,0.08)',
        }}
      >
        {/* Logo + heading (centered) */}
        <Stack alignItems="center" spacing={1.25} sx={{ mb: 3 }}>
          {brand?.logoUrl ? (
            <Box
              component="img" src={brand.logoUrl} alt={brand.companyName}
              sx={{ maxWidth: 180, maxHeight: 64, objectFit: 'contain', borderRadius: 2, bgcolor: 'rgba(255,255,255,0.92)', p: 1 }}
            />
          ) : (
            <Box
              sx={{
                width: 56, height: 56, borderRadius: 3, display: 'grid', placeItems: 'center',
                background: 'linear-gradient(135deg,#4F46E5,#2563EB 55%,#06B6D4)',
                boxShadow: '0 12px 28px rgba(37,99,235,0.5)',
              }}
            >
              <FlightTakeoffRounded sx={{ color: '#fff', fontSize: 28 }} />
            </Box>
          )}
          <Typography sx={{ fontWeight: 800, fontSize: 25, color: '#fff', letterSpacing: '-0.02em', textAlign: 'center' }}>
            Welcome Back <Box component="span" sx={{ WebkitTextFillColor: 'initial' }}>👋</Box>
          </Typography>
          <Typography sx={{ color: 'rgba(203,213,225,0.75)', fontSize: 14, textAlign: 'center' }}>
            {brand ? `Sign in to ${brand.companyName}` : 'Sign in to continue to your account'}
          </Typography>
        </Stack>

        {otp.step ? <LoginOtpPanel otp={otp} dark /> : (
        <Box component="form" onSubmit={onSubmit} noValidate>
          <Stack spacing={2.25}>
            {notice && !error && (
              <Alert severity="info" sx={{ borderRadius: 2, bgcolor: 'rgba(30,58,138,0.35)', color: '#DBEAFE', border: '1px solid rgba(96,165,250,0.4)', '& .MuiAlert-icon': { color: '#93C5FD' } }}>
                {notice}
              </Alert>
            )}

            {error && (
              <Alert severity="error" sx={{ borderRadius: 2, bgcolor: 'rgba(127,29,29,0.35)', color: '#FECACA', border: '1px solid rgba(248,113,113,0.4)', '& .MuiAlert-icon': { color: '#FCA5A5' } }}>
                {error}
              </Alert>
            )}

            <Box>
              {label('Email Address')}
              <Controller
                name="email"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="you@company.com"
                    type="email"
                    fullWidth
                    autoComplete="username"
                    autoFocus={!preview}
                    sx={fieldSx}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      htmlInput: { 'aria-label': 'Email address' },
                      input: { startAdornment: <InputAdornment position="start"><MailOutlineRounded /></InputAdornment> },
                    }}
                  />
                )}
              />
            </Box>

            <Box>
              {label('Password')}
              <Controller
                name="password"
                control={form.control}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    placeholder="••••••••••"
                    type={showPwd ? 'text' : 'password'}
                    fullWidth
                    autoComplete="current-password"
                    sx={fieldSx}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    slotProps={{
                      htmlInput: { 'aria-label': 'Password' },
                      input: {
                        startAdornment: <InputAdornment position="start"><LockOutlined /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={toggleShowPwd} edge="end" size="small"
                              sx={{ color: 'rgba(203,213,225,0.7)' }}
                              aria-label={showPwd ? 'Hide password' : 'Show password'}
                            >
                              {showPwd ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <FormControlLabel
                control={
                  <Checkbox
                    size="small"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#818CF8' } }}
                  />
                }
                label={<Typography variant="body2" sx={{ color: 'rgba(226,232,240,0.85)' }}>Remember me</Typography>}
              />
              <Link component={RouterLink} to="/forgot-password" variant="body2" fontWeight={600} underline="hover" sx={{ color: '#93B4FF' }}>
                Forgot Password?
              </Link>
            </Stack>

            <Button
              type="submit"
              fullWidth
              disabled={busy || preview}
              sx={{
                position: 'relative',
                height: 56,
                borderRadius: '16px',
                textTransform: 'none',
                fontSize: 16,
                fontWeight: 700,
                color: '#fff',
                background: 'linear-gradient(90deg,#4F46E5,#2563EB 55%,#3B82F6)',
                boxShadow: '0 14px 32px rgba(37,99,235,0.5)',
                transition: 'transform .15s ease, box-shadow .2s ease, filter .2s ease',
                '&:hover': { filter: 'brightness(1.07)', boxShadow: '0 18px 40px rgba(37,99,235,0.62)', transform: 'translateY(-1px)' },
                '&.Mui-disabled': { color: 'rgba(255,255,255,0.85)', background: 'linear-gradient(90deg,#4F46E5,#2563EB)', opacity: 0.8 },
              }}
            >
              {busy ? (
                <CircularProgress size={22} sx={{ color: '#fff' }} />
              ) : (
                <>
                  Sign In
                  <ArrowForwardRounded sx={{ position: 'absolute', right: 20, fontSize: 20 }} />
                </>
              )}
            </Button>
          </Stack>
        </Box>
        )}

        {/* Creating a workspace means joining the PRODUCT — offered only on TravelERP's own address. */}
        {!brand && (
          <Typography align="center" variant="body2" sx={{ mt: 3, color: 'rgba(203,213,225,0.8)' }}>
            New to {APP_NAME}?{' '}
            <Link component={RouterLink} to="/onboarding" fontWeight={700} underline="hover" sx={{ color: '#93B4FF' }}>
              Create Workspace
            </Link>
          </Typography>
        )}
      </Box>

      {/* Footer — sits on the photo, just below the glass card */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 2, px: 1 }}>
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <ShieldOutlined sx={{ fontSize: 15, color: 'rgba(148,163,184,0.9)' }} />
          <Typography variant="caption" sx={{ color: 'rgba(203,213,225,0.7)' }}>Secure &amp; Encrypted</Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: 'rgba(148,163,184,0.7)' }}>
          {brand ? `Powered by ${APP_NAME}` : APP_VERSION}
        </Typography>
      </Stack>
    </Box>
  );
}
